/**
 * Typed auth API facade over the generated contract client.
 */

import { authApi } from '../api/generated/auth.api.js';
import {
  type CognitoRegisterRequest,
  type CognitoRegisterResponse,
  type CognitoSyncUserRequest,
  type CognitoSyncUserResponse,
  type MeResponseData,
  type PasswordPolicy,
  type UpdateOwnProfileRequest,
  type UpdateOwnProfileResponse,
  type UploadOwnAvatarResponse,
} from '@cnapse/shared/schemas.js';

class AuthService {
  async register(userData: CognitoRegisterRequest): Promise<CognitoRegisterResponse> {
    return authApi.register(userData) as Promise<CognitoRegisterResponse>;
  }

  async syncCognitoUser(request: CognitoSyncUserRequest): Promise<CognitoSyncUserResponse> {
    return authApi.syncUser(request) as Promise<CognitoSyncUserResponse>;
  }

  async getCurrentUser(): Promise<MeResponseData> {
    try {
      return (await authApi.getMe()) as MeResponseData;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes('Request timeout')) {
          throw new Error('Backend server is not responding - please check if the server is running');
        }
        if (error.message.includes('Network error') || error.message.includes('fetch')) {
          throw new Error('Cannot connect to backend server - please check your network connection');
        }
        if (error.message.includes('401') || error.message.includes('Unauthorized')) {
          throw new Error('Your session has expired - please login again');
        }
        throw error;
      }
      throw new Error('Failed to validate user session');
    }
  }

  async updateOwnProfile(updates: UpdateOwnProfileRequest): Promise<UpdateOwnProfileResponse> {
    const validatedResponse = (await authApi.updateProfile(updates)) as UpdateOwnProfileResponse;
    if (!validatedResponse.success) {
      throw new Error(validatedResponse.message || 'Failed to update profile');
    }
    return validatedResponse;
  }

  async uploadOwnAvatar(file: File): Promise<UploadOwnAvatarResponse> {
    return authApi.uploadAvatar({ file }) as Promise<UploadOwnAvatarResponse>;
  }

  async deleteOwnAvatar(): Promise<void> {
    await authApi.deleteAvatar();
  }

  async getPasswordPolicy(): Promise<PasswordPolicy> {
    try {
      return (await authApi.getPasswordPolicy()) as PasswordPolicy;
    } catch {
      return {
        minimumLength: 8,
        requireUppercase: true,
        requireLowercase: true,
        requireNumbers: true,
        requireSymbols: true,
        temporaryPasswordValidityDays: 7,
      };
    }
  }
}

export const authService = new AuthService();
